import React, { useEffect, useRef, FC } from 'react';
import { connect } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  CardOverview,
  Container,
  Flex,
  FlexItem,
  H2,
  styled,
  ContainerProps,
} from '@erecruit/ui-kit';
import { Intl } from '@erecruit/core';
import { Authorize } from '@erecruit/modules';
import { AppPermissions } from '@erecruit/types';
import {
  GeneralSection,
  BonusSection,
  ScheduleSection,
  ShiftWidget,
  ShiftRecordHeader,
} from './components';
import { ShiftRecordProps } from './types';
import { RootState } from '../../redux/reducers';
import { InsightSpinner } from '../../App/components/InsightPortal';
import { selectors, fetchShiftRecord } from './state/reducer';

const { FormattedMessage, injectIntl } = Intl;

const ShiftWidgetContainer = styled(Container)<ContainerProps>(({ theme }) => ({
  backgroundColor: theme.newColors.bgStandard,
  marginRight: 0,
  padding: '16px 16px 40px!important',
}));

const DetailsContainer = styled(Container)<ContainerProps>({
  margin: '-30px 16px 0px 16px!important',
  background: 'transparent',
});

const ShiftRecord: FC<ShiftRecordProps> = props => {
  const { id } = useParams();
  const { infoSection, sections, fetchShiftRecord, isLoadingInfo } = props;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetchShiftRecord(Number(id));
  }, [fetchShiftRecord, id]);

  if (isLoadingInfo) {
    return <InsightSpinner />;
  }

  if (!infoSection || !sections) return null;

  return (
    <Container
      padding={[4, 0, 0, 0]}
      gutter={4}
      background="pageSecondary"
      stretch
      ref={containerRef}
    >
      <Flex direction="column" gutter={2} gutterDirection="vertical">
        <FlexItem padding={[0, 12, 10, 12]}>
          <ShiftRecordHeader
            isLoadingInfo={isLoadingInfo}
            infoSection={infoSection}
            fetchShiftRecord={fetchShiftRecord}
          />
        </FlexItem>
      </Flex>
      <Container gutter={4} stretch={false} background="pageSecondary">
        <ShiftWidgetContainer>
          <ShiftWidget data={sections.general} />
        </ShiftWidgetContainer>
        <DetailsContainer padding={[0, 12, 12, 12]} gutter={4} stretch>
          <CardOverview
            title={
              <H2 color="headerPrimary">
                <FormattedMessage
                  id={messageIds.vms.shiftRecord.sectionsTitle}
                />
              </H2>
            }
            extraComponent={null}
          >
            <Flex gutter={4}>
              <FlexItem basis="50%">
                <Flex direction="column" gutter={4} gutterDirection="vertical">
                  <ScheduleSection data={sections.schedule} />
                  <Authorize permission={AppPermissions.ViewShiftBonus}>
                    <BonusSection data={sections.bonus} />
                  </Authorize>
                </Flex>
              </FlexItem>
              <FlexItem basis="50%">
                <Flex direction="column" gutter={4} gutterDirection="vertical">
                  <GeneralSection data={sections.general} />
                </Flex>
              </FlexItem>
            </Flex>
          </CardOverview>
        </DetailsContainer>
      </Container>
    </Container>
  );
};

export default injectIntl(
  connect(
    (state: RootState) => ({
      isLoadingInfo: selectors.selectRequestShiftState(state).isLoadingInfo,
      infoSection: selectors.selectRequestShiftState(state).infoSection,
      sections: selectors.selectRequestShiftState(state).sections,
    }),
    { fetchShiftRecord },
  )(ShiftRecord),
);
